import React, { FC, useEffect, useMemo, useState } from 'react'
import { PmsJobRecord } from '../../core/pms/_models'
import { recordPlanLineLastDone } from '../../core/pms/_requests'
import { toast } from 'react-toastify'

interface Props {
  visible: boolean
  onClose: () => void
  job: PmsJobRecord
  currentCounterValue?: number | null
  onSaved: () => void
}

const WRAP_STYLE: React.CSSProperties = {
  background: 'rgba(0,0,0,0.5)',
  position: 'fixed',
  inset: 0,
  zIndex: 2100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const LABEL = 'form-label fw-semibold fs-6 mb-2 text-dark'

type LastDoneMode = 'hours' | 'date'

const resolveScheduleKind = (job: PmsJobRecord): 'hours' | 'date' | 'hybrid' => {
  const type = job.scheduleType || ''
  if (type === 'RUNNING_HOURS') return 'hours'
  if (type === 'TIME') return 'date'
  if (type === 'HYBRID_WHICHEVER_FIRST' || type === 'HYBRID_BOTH_REQUIRED') return 'hybrid'
  const ids = [job.periodicityId, job.periodicityId2].filter(Boolean).map((v) => v!.toUpperCase())
  const hasHours = ids.includes('H')
  const hasTime = ids.some((v) => v === 'M' || v === 'W' || v === 'D')
  if (hasHours && hasTime) return 'hybrid'
  if (hasHours) return 'hours'
  return 'date'
}

export const PlanLineLastDoneModal: FC<Props> = ({ visible, onClose, job, currentCounterValue, onSaved }) => {
  const todayStr = new Date().toISOString().split('T')[0]
  const scheduleKind = useMemo(() => resolveScheduleKind(job), [job])
  const supportsHours = scheduleKind === 'hours' || scheduleKind === 'hybrid'
  const supportsDate = scheduleKind === 'date' || scheduleKind === 'hybrid'
  const defaultMode: LastDoneMode = supportsHours ? 'hours' : 'date'
  const [mode, setMode] = useState<LastDoneMode>(defaultMode)
  const [hoursValue, setHoursValue] = useState<string>('')
  const [dateValue, setDateValue] = useState<string>(todayStr)
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  useEffect(() => {
    if (!visible) return
    setMode(defaultMode)
    setHoursValue('')
    setDateValue(todayStr)
    setErrors({})
  }, [visible, defaultMode, todayStr])

  if (!visible) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: { [key: string]: string } = {}

    if (!job.planLineId) {
      toast.error('This job is not linked to a plan line', { position: 'top-center' })
      return
    }

    if (mode === 'hours') {
      const numeric = Number(hoursValue)
      if (!hoursValue || Number.isNaN(numeric)) {
        newErrors.hours = 'Hours last done is required'
      } else if (numeric < 0) {
        newErrors.hours = 'Hours last done must be 0 or greater'
      } else if (currentCounterValue != null && numeric > currentCounterValue) {
        newErrors.hours = `Hours last done cannot exceed current reading (${currentCounterValue.toLocaleString()} hrs)`
      }
    } else {
      if (!dateValue) {
        newErrors.date = 'Last done date is required'
      } else {
        const selected = new Date(dateValue)
        const today = new Date(todayStr)
        selected.setHours(0, 0, 0, 0)
        today.setHours(0, 0, 0, 0)
        if (selected > today) {
          newErrors.date = 'Future dates are not allowed'
        }
      }
    }

    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      const firstError = Object.values(newErrors)[0]
      if (firstError) {
        toast.error(firstError, { position: 'top-center' })
      }
      return
    }

    const payload =
      mode === 'hours'
        ? { lastDoneCounter: Number(hoursValue), lastDoneDate: null }
        : { lastDoneDate: dateValue, lastDoneCounter: null }

    setSubmitting(true)
    try {
      await recordPlanLineLastDone(job.planLineId, payload)
      toast.success('Last done recorded successfully', { position: 'top-center' })
      onSaved()
      onClose()
    } catch (error: any) {
      console.error('Failed to record last done', error)
      toast.error('Failed to record last done', { position: 'top-center' })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div style={WRAP_STYLE} tabIndex={-1} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className='bg-white text-dark rounded shadow-lg d-flex flex-column'
        style={{ width: '520px', maxWidth: '90vw' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='d-flex align-items-center justify-content-between border-bottom px-4 py-3'>
          <h5 className='modal-title text-dark m-0'>Add Last Done</h5>
          <button type='button' className='btn-close' onClick={onClose}></button>
        </div>

        <form onSubmit={handleSubmit} className='px-4 py-3'>
          <div className='mb-3'>
            <div className='fw-semibold'>{job.jobDescription || job.title || '-'}</div>
            <div className='text-muted fs-7'>
              {job.jobCode ? `Job Code: ${job.jobCode} - ` : ''}
              {job.equipmentName || 'Equipment'} {job.subComponentName ? `- ${job.subComponentName}` : ''}
            </div>
          </div>

          {scheduleKind === 'hybrid' && (
            <div className='mb-3'>
              <label className={LABEL}>Record Last Done By</label>
              <div className='d-flex gap-4'>
                {supportsHours && (
                  <label className='form-check d-flex align-items-center gap-2'>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='lastDoneMode'
                      value='hours'
                      checked={mode === 'hours'}
                      onChange={() => setMode('hours')}
                    />
                    <span className='form-check-label'>Running Hours</span>
                  </label>
                )}
                {supportsDate && (
                  <label className='form-check d-flex align-items-center gap-2'>
                    <input
                      className='form-check-input'
                      type='radio'
                      name='lastDoneMode'
                      value='date'
                      checked={mode === 'date'}
                      onChange={() => setMode('date')}
                    />
                    <span className='form-check-label'>Completion Date</span>
                  </label>
                )}
              </div>
            </div>
          )}

          {mode === 'hours' && supportsHours && (
            <div className='mb-3'>
              <label className={LABEL}>Hours Last Done</label>
              <input
                type='number'
                min={0}
                className='form-control text-dark'
                value={hoursValue}
                onChange={(e) => setHoursValue(e.target.value)}
                placeholder='Enter running hours'
              />
              {errors.hours && <div className='text-danger fs-8 mt-1'>{errors.hours}</div>}
            </div>
          )}

          {mode === 'date' && supportsDate && (
            <div className='mb-3'>
              <label className={LABEL}>Last Done Date</label>
              <input
                type='date'
                max={todayStr}
                className='form-control text-dark'
                value={dateValue}
                onChange={(e) => setDateValue(e.target.value)}
              />
              {errors.date && <div className='text-danger fs-8 mt-1'>{errors.date}</div>}
            </div>
          )}

          <div className='d-flex justify-content-end gap-2 pt-2'>
            <button type='button' className='btn btn-light' onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button type='submit' className='btn btn-primary' disabled={submitting}>
              {submitting ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
